import {Helper} from "@nolimitcity/promo-panel/bin/utils/Helper";
import {WINTYPE} from "@nolimitcity/promo-panel/bin/enums/ASEnums";
import {
    ActionSpinOptions,
    ActionSpinSound,
    ASWinData,
    IResponseData,
    PromoPanelGameConfiguration,
} from "@nolimitcity/promo-panel/bin/interfaces/ASInterfaces";
import {TombOfAkhenaten} from "./TombOfAkhenaten";

import {TombOfAkhenatenGameAssets} from "./TombOfAkhenatenGameAssets";
import {TombOfAkhenatenGameModuleConfig} from "./TombOfAkhenatenGameModuleConfig";
import {TombOfAkhenatenGameMode} from "./gameMode/TombOfAkhenatenGameMode";
import {SoundManager} from "./sound/SoundManager";
import {TombOfAkhenatenSoundConfig} from "./TombOfAkhenatenSoundConfig";
import {ITombOfAkhenatenGameData} from "./server/data/ITombOfAkhenatenServerData";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TimelineLite} from "gsap";
import {TOAGameEvent} from "./events/TOAGameEvent";
import {TombOfAkhenatenReelSymbol} from "./symbols/TombOfAkhenatenReelSymbol";
import {TombOfAkhenatenGameConfig} from "./TombOfAkhenatenGameConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";

export class TombOfAkhenatenPromoPanelConfig implements PromoPanelGameConfiguration {

    constructor() {
    }

    public getActionSpinOptions(): ActionSpinOptions {
        return {
            gameName: "TombOfAkhenaten",
            hasActionSpin: true,
            graphics: {backgroundTextureName: TombOfAkhenatenGameAssets.MAIN_GAME_BACKGROUND},
            getParsedData: (response: ITombOfAkhenatenGameData, bet: number, prevResponse: ITombOfAkhenatenGameData): ASWinData => {
                const parsedData: ASWinData = {
                    totalWin: parseFloat(response.accumulatedRoundWin),
                    totalSpinWinnings: this.getWin(response, bet, prevResponse),
                    wasFeatureBuy: response.wasFeatureBuy,
                    freeSpinTriggeredThisSpin: this.isFreeSpinTriggered(response),
                    isBigWin: this.isBigWin(response),
                    winType: WINTYPE.NORMAL,
                    featureName: this.getFeatureName(response),
                    isRoundComplete: this.isRoundComplete(response),
                    isBonusEnd: this.isBonusEnd(response),
                    isWinCapHit: false
                }
                return parsedData;
            }
        };
    }

    public onActionSpinPlaySound(trigger: ActionSpinSound): void {
        if (trigger == ActionSpinSound.WIN) {
            SoundManager.playSound(TombOfAkhenatenSoundConfig.instance.AS_WIN);
        } else if (trigger == ActionSpinSound.BIG_WIN) {
            SoundManager.playSound(TombOfAkhenatenSoundConfig.instance.AS_WIN_BIG);
        }
    }

    isLegalStopOnBonusMode(response: IResponseData): boolean {
        return (response.mode == TombOfAkhenatenGameMode.NORMAL || response.mode === TombOfAkhenatenGameMode.RESPIN_FIRST || response.mode === TombOfAkhenatenGameMode.RESPIN_SECOND || response.mode === TombOfAkhenatenGameMode.RESPIN_FINAL) && response.nextMode == TombOfAkhenatenGameMode.FREESPIN;
    }

    onPlayBonusInGame(data: any): void {
        const tl: TimelineLite = new TimelineLite();
        this.restoreBaseGameReels(data);
        EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.UPDATE_STONE_WAYS, data.activeRows));
        EventHandler.dispatchEvent(new GameEvent("stopIndividualWinForAS", true));
        EventHandler.dispatchEvent(new GameEvent("updateMultiForAS", data.payoutMultiplier));
        EventHandler.dispatchEvent(new GameEvent("updateReelsForAs", data.activeRows));
        EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.REMOVE_SCATTER_ANIMS));
        TombOfAkhenatenGameModuleConfig.reelDividerView.setDividerPositions(data.activeRows);
        tl.add(() => {
            EventHandler.dispatchEvent(new GameEvent(ServerEvent.GAME_DATA_RECEIVED, data));
            if (data.accumulatedRoundWin > 0) {
                SlotGame.winFieldController.showWinField(data.accumulatedRoundWin, false, TombOfAkhenatenGameModuleConfig.featurePrice.isWinBelowStakeForThisWin(data.accumulatedRoundWin, data));
            } else {
                SlotGame.keypad.setWin(0);
            }
            //461 second point
            StageManager.getLayer(TombOfAkhenatenGameConfig.instance.LAYERS.SYMBOLS.name).children.forEach((item) => {
                if (item) {
                    if (item.name! && item.name!.includes("Text")) {
                        item.destroy();
                    }
                }
            })
            NolimitApplication.events.trigger("checkTransitionForActionSpins");
        });
    }

    private isBigWin(response: ITombOfAkhenatenGameData): boolean {
        const win: number = parseFloat(response.totalSpinWinnings);
        const playedBetValue: number = parseFloat(response.playedBetValue);
        let winRatio: number = Helper.ceilToDecimals((win / playedBetValue), 5);

        if (response.wasFeatureBuy) {
            const betPrice = TombOfAkhenatenGameModuleConfig.featurePrice.getBuyFeaturePrice(response)
            winRatio = Helper.ceilToDecimals((win / betPrice), 5);
            return winRatio >= 1;
        }

        return winRatio >= 15;
    }

    private getWin(response: ITombOfAkhenatenGameData, bet: number, prevData: ITombOfAkhenatenGameData): number {
        return parseFloat(response.totalSpinWinnings);
    }

    private getFeatureName(response: ITombOfAkhenatenGameData): string {
        if (response.nextMode == TombOfAkhenatenGameMode.FREESPIN || response.nextMode == TombOfAkhenatenGameMode.FS_RESPIN_FINAL || response.nextMode == TombOfAkhenatenGameMode.FS_RESPIN_SECOND || response.nextMode == TombOfAkhenatenGameMode.FS_RESPIN_FIRST || response.mode == TombOfAkhenatenGameMode.FREESPIN || response.mode == TombOfAkhenatenGameMode.FS_RESPIN_FINAL || response.mode == TombOfAkhenatenGameMode.FS_RESPIN_SECOND || response.mode == TombOfAkhenatenGameMode.FS_RESPIN_FIRST) {
            return "AKHENATEN SPINS";
        }
        return "";
    }

    private isFreeSpinTriggered(response: ITombOfAkhenatenGameData): boolean {
        return (response.mode == TombOfAkhenatenGameMode.NORMAL || response.mode === TombOfAkhenatenGameMode.RESPIN_FIRST || response.mode === TombOfAkhenatenGameMode.RESPIN_SECOND || response.mode === TombOfAkhenatenGameMode.RESPIN_FINAL) && response.nextMode == TombOfAkhenatenGameMode.FREESPIN;
    }

    private restoreBaseGameReels(data: any): void {
        const reels: string[][] = JSON.parse(JSON.stringify(data.evaluatedAreaUpdated));
        let sym: TombOfAkhenatenReelSymbol;
        for (let i: number = 0; i < reels.length; i++) {
            for (let j: number = 0; j < reels[i].length; j++) {
                sym = (<TombOfAkhenatenReelSymbol>Reels.getSymbol((i * 4) + (j + i), 0)!);
                if (reels[i][j] === "X") {
                    continue;
                }
                sym.changeState({
                    state: TombOfAkhenatenGameConfig.instance.SYMBOL_STATES.normal,
                    setStack: true,
                    fadeInDuration: 0.0
                })
                sym.removeChildren();
                sym.changeTextureForAS(reels[i][j], data.activeRows);
            }
        }
    }

    private isRoundComplete(response: ITombOfAkhenatenGameData): boolean {
        return ([TombOfAkhenatenGameMode.NORMAL, TombOfAkhenatenGameMode.FREESPIN].indexOf(response.nextMode) > -1);
    }


    private isBonusEnd(response: ITombOfAkhenatenGameData): boolean {
        return (response.mode === TombOfAkhenatenGameMode.FREESPIN || response.mode === TombOfAkhenatenGameMode.FS_RESPIN_FIRST || response.mode === TombOfAkhenatenGameMode.FS_RESPIN_SECOND || response.mode === TombOfAkhenatenGameMode.FS_RESPIN_FINAL) && response.nextMode === TombOfAkhenatenGameMode.NORMAL;
    }

}